import { ChevronRight, Search } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";

export function CapabilitySummary({
	title,
	summary,
	onOpen,
}: {
	title: string;
	summary: string;
	onOpen: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onOpen}
			className="group flex w-full items-center justify-between gap-4 rounded-lg border border-border px-4 py-3 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
		>
			<span className="min-w-0">
				<span className="block text-sm font-medium">{title}</span>
				<span className="mt-0.5 block text-xs text-muted-foreground">
					{summary}
				</span>
			</span>
			<ChevronRight
				aria-hidden="true"
				className="h-4 w-4 shrink-0 text-muted-foreground"
			/>
		</button>
	);
}

interface CapabilityEntry {
	id: string;
	name: string;
	description?: string;
	status?: string;
}

export function CapabilitySearch({
	label,
	query,
	onQueryChange,
	count,
	action,
}: {
	label: string;
	query: string;
	onQueryChange: (query: string) => void;
	count: number;
	action?: ReactNode;
}) {
	return (
		<>
			<div className="flex items-center gap-2">
				<div className="relative min-w-0 flex-1">
					<Search
						aria-hidden="true"
						className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
					/>
					<Input
						type="search"
						aria-label={`Search ${label}`}
						placeholder={`Search ${label}…`}
						value={query}
						onChange={(event) => onQueryChange(event.target.value)}
						className="pl-9"
					/>
				</div>
				{action}
			</div>
			<p className="text-xs text-muted-foreground" role="status">
				{count} {count === 1 ? label.replace(/s$/, "") : label}
				{query.trim() ? " found" : ""}
			</p>
		</>
	);
}

/** Searchable accordions keep policy controls visible and opened form drafts mounted. */
export function CapabilityBrowser<T extends CapabilityEntry>({
	entries,
	label,
	action,
	renderControl,
	renderDetail,
}: {
	entries: T[];
	label: string;
	action?: ReactNode;
	renderControl?: (entry: T) => ReactNode;
	renderDetail: (entry: T) => ReactNode;
}) {
	const [query, setQuery] = useState("");
	const matching = new Set(
		entries
			.filter((entry) =>
				`${entry.name} ${entry.description ?? ""}`
					.toLowerCase()
					.includes(query.trim().toLowerCase()),
			)
			.map((entry) => entry.id),
	);
	return (
		<div className="space-y-4">
			<CapabilitySearch
				label={label}
				query={query}
				onQueryChange={setQuery}
				count={matching.size}
				action={action}
			/>
			<ul>
				{entries.map((entry) => (
					<CapabilityAccordion
						key={entry.id}
						entry={entry}
						hidden={!matching.has(entry.id)}
						control={renderControl?.(entry)}
						renderDetail={() => renderDetail(entry)}
					/>
				))}
			</ul>
			{matching.size === 0 && (
				<p className="py-5 text-sm text-muted-foreground">
					{query ? "No matches. Try another search." : `No ${label} yet.`}
				</p>
			)}
		</div>
	);
}

function CapabilityAccordion({
	entry,
	hidden,
	control,
	renderDetail,
}: {
	entry: CapabilityEntry;
	hidden: boolean;
	control?: ReactNode;
	renderDetail: () => ReactNode;
}) {
	const [open, setOpen] = useState(false);
	const [visited, setVisited] = useState(false);
	return (
		<li hidden={hidden} className="border-b border-border last:border-b-0">
			<Collapsible
				open={open}
				onOpenChange={(next) => {
					setOpen(next);
					if (next) setVisited(true);
				}}
			>
				<div className="flex items-center gap-3 py-3">
					<CollapsibleTrigger className="group flex min-w-0 flex-1 items-start gap-2 rounded-sm text-left hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
						<ChevronRight
							aria-hidden="true"
							className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90 motion-reduce:transition-none"
						/>
						<span className="min-w-0">
							<span className="block text-sm font-medium">{entry.name}</span>
							{entry.status && (
								<span className="mt-0.5 block text-xs text-muted-foreground">
									{entry.status}
								</span>
							)}
						</span>
					</CollapsibleTrigger>
					{control && <div className="shrink-0">{control}</div>}
				</div>
				{visited && (
					<CollapsibleContent
						forceMount
						hidden={!open}
						className="space-y-4 pb-4 pl-6"
					>
						{entry.description && (
							<p className="text-sm text-muted-foreground">
								{entry.description}
							</p>
						)}
						{renderDetail()}
					</CollapsibleContent>
				)}
			</Collapsible>
		</li>
	);
}
